import React, { useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { Chart } from "chart.js/auto";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

import axios from "axios";
import { baseUrl, baseimgUrl, userName, ipAddress } from './Univ.js';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';

function Home() {
  const [reports, setReports] = useState([]);
  const [booths, setBooths] = useState([]);
  const [events, setEvents] = useState([]);
  const [newsview, setNewsView] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const currentDate = new Date().toLocaleDateString();
  const chartRef = useRef(null);
const COLORS = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728",
  "#9467bd", "#8c564b", "#e377c2", "#7f7f7f",
  "#bcbd22", "#17becf",

  "#393b79", "#637939", "#8c6d31", "#843c39",
  "#7b4173", "#3182bd", "#31a354", "#756bb1",
  "#636363", "#e6550d",

  "#969696", "#9c9ede", "#cedb9c", "#e7ba52",
  "#e7969c", "#de9ed6", "#6baed6", "#74c476",
  "#9e9ac8", "#bdbdbd",

  "#fd8d3c", "#a1d99b", "#bcbddc", "#c7c7c7",
  "#fdae6b", "#74c476", "#fd8d3c", "#756bb1"
];

  useEffect(() => {
    fetchReports();
    fetchBooths();
    fetchEventsData();
    fetchNews();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${baseUrl}boothreports/`);
      setReports(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBooths = async () => {
    try {
      const res = await axios.get(`${baseUrl}booths/`);
      setBooths(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchEventsData = async () => {
  try {
    setIsLoading(true);
    const response = await axios.get(`${baseUrl}get-events/`);
    const eventsData = response.data;
    const formattedEvents = eventsData.map((event) => {
  const start = toZonedTime(new Date(event.start_date), 'Asia/Kolkata');
  const end = toZonedTime(new Date(event.end_date), 'Asia/Kolkata');
  return {
    id: event.id,
    title: event.title,
    start,
    end,
  };
});

    setTimeSlots(formattedEvents);
    setEvents(formattedEvents);
  } catch (error) {
    console.error('Error fetching events data:', error);
    alert('Failed to fetch events. Please try again.');
  } finally {
    setIsLoading(false);
  }
};

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${baseUrl}view-news/`);
      setNewsView(res.data);

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
  if (!reports.length || !chartRef.current) return;

  const ctx = chartRef.current.getContext("2d");

  // Build data
  const labels = [];
  const values = [];
  const yLabels = [];

  reports.forEach(r => {
    const boothNo = r.booth?.booth_number || "Unknown Booth";
    const constituency = r.booth?.constituency?.name || "Unknown";
    const district = r.booth?.constituency?.district || "Unknown";

    labels.push(`Booth ${boothNo}`);
    values.push(1); // one issue per booth
    yLabels.push(`${constituency} - ${district}`);
  });

  const chart = new Chart(ctx, {
    type: "bar",
    data: {
      labels: labels,
      datasets: [
        {
          label: "Booth Issues",
          data: values,
          backgroundColor: "rgba(54,162,235,0.6)",
        }
      ]
    },
    options: {
      responsive: true,
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              return yLabels[context.dataIndex];
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Booth Number"
          }
        },
        y: {
          ticks: {
            stepSize: 1
          },
          title: {
            display: true,
            text: "Issue Count"
          }
        }
      }
    }
  });

  return () => chart.destroy();
}, [reports]);

const [pieData, setPieData] = useState([]);
const [level, setLevel] = useState("district"); // district | constituency | voters
const [history, setHistory] = useState([]);

useEffect(() => {
  axios.get(`${baseUrl}pie/districts/`)
    .then(res => setPieData(res.data));
}, []);

const handlePieClick = (data) => {
  setHistory(prev => [...prev, { level, pieData }]);

  if (level === "district") {
    axios
      .get(`${baseUrl}pie/constituencies/${data.name}/`)
      .then(res => {
        setPieData(res.data);
        setLevel("constituency");
      });
  }

  else if (level === "constituency") {
    axios
      .get(`${baseUrl}pie/constituency-voters/${data.id}/`)
      .then(res => {
        setPieData(res.data);
        setLevel("voters");
      });
  }
};
const handleBack = () => {
  const last = history[history.length - 1];
  if (!last) return;

  setPieData(last.pieData);
  setLevel(last.level);
  setHistory(prev => prev.slice(0, -1));
};
{history.length > 0 && (
  <button
    className="btn btn-outline-secondary mb-2"
    onClick={handleBack}
  >
    ← Back
  </button>
)}
const handleLegendClick = (e) => {
  // e.value → legend name
  // e.payload → full data object

  setHistory(prev => [...prev, { level, pieData }]);

  if (level === "district") {
    axios
      .get(`${baseUrl}pie/constituencies/${e.value}/`)
      .then(res => {
        setPieData(res.data);
        setLevel("constituency");
      });
  }

  else if (level === "constituency") {
    axios
      .get(`${baseUrl}pie/constituency-voters/${e.payload.id}/`)
      .then(res => {
        setPieData(res.data);
        setLevel("voters");
      });
  }
};
// Booths that submitted turnout (unique booths)
const turnoutBooths = [
  ...new Set(reports.map(r => r.booth?.id).filter(Boolean))
];

// Total voters polled
const totalVoted = reports.reduce((sum, r) => {
  return (
    sum +
    Number(r.male_voted || 0) +
    Number(r.female_voted || 0) +
    Number(r.third_voted || 0)
  );
}, 0);

// Open incidents (severity >= 2)
const openIncidents = reports.filter(r => Number(r.severity || 0) >= 2);

  return (
    <section className="container mt-4">
      <h2>Election Dashboard-  {currentDate}</h2>

      <div className="row mt-4 g-3">
  {/* Total Booths */}
  <div className="col-md-3 col-sm-6">
    <div className="dashboard-card bg-primary">
      <h6>Total Booths</h6>
      <h2>{booths.length}</h2>
      <span>Across all constituencies</span>
    </div>
  </div>

  {/* Booths Reporting */}
  <div className="col-md-3 col-sm-6">
    <div className="dashboard-card bg-success">
      <h6>Booths Reporting</h6>
      <h2>{turnoutBooths.length}</h2>
      <span>Submitted turnout today</span>
    </div>
  </div>

  {/* Total Voters Polled */}
  <div className="col-md-3 col-sm-6">
    <div className="dashboard-card bg-warning">
      <h6>Voters Polled</h6>
      <h2>{totalVoted}</h2>
      <span>Male + Female + Third</span>
    </div>
  </div>

  {/* Open Incidents */}
  <div className="col-md-3 col-sm-6">
    <div className="dashboard-card bg-danger">
      <h6>Open Incidents</h6>
      <h2>{openIncidents.length}</h2>

      <span>Medium & High severity</span>
    </div>
  </div>
</div>


      <div className="row mt-5">
        <div className="col-md-6">
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            events={events}
          />
        </div>

<div className="col-md-6 mb-2 mt-5">
                <div className="news-ticker-container">
                  <div className="news-ticker-box">
                    <div className="news-ticker" id="newsTicker">
                      {newsview.map((newsviewData, index) => (
                        <div key={index} className="news-item">
                          <h2><img src={newsviewData.attachment} alt="Thumbnail" className="thumbnail" style={{width:120, height:60}}/></h2>
                          <div className="news-text">
                            <h4>{newsviewData.heading}</h4>
                            <p>{newsviewData.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
      </div>

      <div className="row mt-5">
        <div className="col-md-6">
          <canvas ref={chartRef}></canvas>
        </div>
  <div className="col-md-6">
<div className="table-responsive">

  {history.length > 0 && (
    <button
      className="btn btn-outline-secondary mb-2"
      onClick={handleBack}
    >
      ← Back
    </button>
  )}

  <h4 className="text-center mb-3">
    {level === "district" && "District-wise Voters"}
    {level === "constituency" && "Constituency-wise Voters"}
    {level === "voters" && "Voter Distribution"}
  </h4>

   <ResponsiveContainer width={520} height={520}>
    <PieChart>
      <Pie
        data={pieData}
        dataKey="value"
        nameKey="name"
        outerRadius={140}
        label={level === "voters"}
        onClick={handlePieClick}
      >
        {pieData.map((_, index) => (
          <Cell key={index} fill={COLORS[index % COLORS.length]} />
        ))}
      </Pie>

      <Tooltip formatter={(v) => v.toLocaleString()} />

      <Legend
        layout="vertical"
        align="right"
        verticalAlign="middle"
        wrapperStyle={{ maxHeight: 350, overflowY: "auto" }}
        onClick={handleLegendClick}
      />
    </PieChart>
  </ResponsiveContainer>
</div>
</div>

</div>

    </section>
  );
}

export default Home;
